import React, { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import {
  CsvValidationResultView,
  CsvRowValidation,
  BulkImportResultView,
  validateCsvImport,
  importCsvAssets,
} from "../../../../api/General/assets";

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type ModalStep = "upload" | "validating" | "results" | "importing" | "done";

const CSV_TEMPLATE = `Name,Category,SerialNumber,PurchasePrice,PurchaseDate,WarrantyExpiry,Condition,Location,Notes
Laptop Dell XPS 15,Electronics,SN-001,1200.00,2024-01-15,2027-01-15,Excellent,Office A,Company laptop
Office Desk,Furniture,SN-002,350.00,2024-03-01,,Good,Floor 2,Standing desk`;

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  open,
  onClose,
  onImportComplete,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<ModalStep>("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validation, setValidation] = useState<CsvValidationResultView | null>(null);
  const [importResult, setImportResult] = useState<BulkImportResultView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setStep("upload");
    setSelectedFile(null);
    setDragActive(false);
    setValidation(null);
    setImportResult(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.name.endsWith(".csv")) {
      setError(t("assets.csvOnly"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t("assets.csvTooLarge"));
      return;
    }
    setError(null);
    setSelectedFile(file);
  }, [t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setValidation(null);
    setError(null);
    setStep("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleValidate = useCallback(async () => {
    if (!selectedFile) return;
    setStep("validating");
    setError(null);

    try {
      const result = await validateCsvImport(selectedFile);
      if (result.errors.length === 0) {
        setValidation(result.payload);
        setStep("results");
      } else {
        setError(result.errors[0]?.description || t("assets.csvValidationFailed"));
        setStep("upload");
      }
    } catch {
      setError(t("assets.csvValidationFailed"));
      setStep("upload");
    }
  }, [selectedFile, t]);

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;
    setStep("importing");
    setError(null);

    try {
      const result = await importCsvAssets(selectedFile);
      if (result.errors.length === 0) {
        setImportResult(result.payload);
        setStep("done");
        onImportComplete();
      } else {
        setError(result.errors[0]?.description || t("assets.csvImportFailed"));
        setStep("results");
      }
    } catch {
      setError(t("assets.csvImportFailed"));
      setStep("results");
    }
  }, [selectedFile, onImportComplete, t]);

  const handleDownloadTemplate = useCallback(() => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "asset_import_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("assets.bulkImport")}</DialogTitle>
          <DialogDescription>{t("assets.bulkImportDescription")}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* Error banner */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {/* Step: Upload */}
          {(step === "upload" || step === "validating") && (
            <div className="space-y-4">
              {/* Template download */}
              <div className="flex items-center justify-between p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="text-sm text-primary">
                  <span className="font-medium">{t("assets.csvTemplateHint")}</span>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="text-sm font-medium text-primary hover:text-primary/80 underline whitespace-nowrap"
                >
                  {t("assets.downloadTemplate")}
                </button>
              </div>

              {/* Drop zone */}
              {!selectedFile ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    dragActive
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-foreground/30 bg-muted"
                  }`}
                >
                  <svg className="w-10 h-10 mx-auto text-muted-foreground mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-muted-foreground font-medium">{t("assets.csvDropzone")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("assets.csvDropzoneHint")}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleInputChange}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted border border-border rounded-lg">
                  <svg className="w-8 h-8 text-success shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    disabled={step === "validating"}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Instructions */}
              <div className="text-xs text-muted-foreground space-y-1">
                <p>{t("assets.csvInstructions1")}</p>
                <p>{t("assets.csvInstructions2")}</p>
                <p>{t("assets.csvInstructions3")}</p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={handleClose}>
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleValidate}
                  disabled={!selectedFile}
                  loading={step === "validating"}
                >
                  {step === "validating" ? t("assets.validating") : t("assets.validateFile")}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Validation Results */}
          {step === "results" && validation && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <SummaryCard
                  label={t("assets.totalRows")}
                  value={validation.totalRows}
                  color="gray"
                />
                <SummaryCard
                  label={t("assets.validRows")}
                  value={validation.validRows}
                  color="green"
                />
                <SummaryCard
                  label={t("assets.invalidRows")}
                  value={validation.invalidRows}
                  color="red"
                />
              </div>

              {/* Errors */}
              {validation.errors.length > 0 && (
                <ValidationSection
                  title={t("assets.errors")}
                  items={validation.errors}
                  color="red"
                />
              )}

              {/* Warnings */}
              {validation.warnings.length > 0 && (
                <ValidationSection
                  title={t("assets.warnings")}
                  items={validation.warnings}
                  color="yellow"
                />
              )}

              {validation.validRows === 0 && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                  {t("assets.noValidRows")}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={handleRemoveFile}>
                  {t("assets.uploadDifferentFile")}
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={validation.validRows === 0}
                >
                  {t("assets.importValidRows", { count: validation.validRows })}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === "importing" && (
            <div className="py-8 text-center space-y-4">
              <svg className="w-10 h-10 mx-auto text-primary animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm font-medium text-foreground">{t("assets.importing")}</p>
            </div>
          )}

          {/* Step: Done */}
          {step === "done" && importResult && (
            <div className="space-y-4">
              <div className="py-4 text-center">
                <svg className="w-12 h-12 mx-auto text-success mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg font-semibold text-foreground">{t("assets.importComplete")}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("assets.importedCount", { count: importResult.importedCount })}
                  {importResult.skippedCount > 0 &&
                    ` · ${t("assets.skippedCount", { count: importResult.skippedCount })}`}
                </p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleClose}>
                  {t("common.close")}
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

function SummaryCard({ label, value, color }: { label: string; value: number; color: "gray" | "green" | "red" }) {
  const bg = { gray: "bg-muted", green: "bg-success/10", red: "bg-destructive/10" }[color];
  const text = { gray: "text-foreground", green: "text-success", red: "text-destructive" }[color];
  const border = { gray: "border-border", green: "border-success/20", red: "border-destructive/20" }[color];

  return (
    <div className={`p-3 rounded-lg border ${bg} ${border}`}>
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className={`text-xl font-bold ${text}`}>{value}</p>
    </div>
  );
}

function ValidationSection({ title, items, color }: { title: string; items: CsvRowValidation[]; color: "red" | "yellow" }) {
  const bg = color === "red" ? "bg-destructive/10" : "bg-warning/10";
  const border = color === "red" ? "border-destructive/20" : "border-warning/20";
  const text = color === "red" ? "text-destructive" : "text-warning";
  const iconColor = color === "red" ? "text-destructive/60" : "text-warning/60";

  return (
    <div className={`rounded-lg border ${border} overflow-hidden`}>
      <div className={`px-3 py-2 ${bg} border-b ${border}`}>
        <h4 className={`text-sm font-medium ${text}`}>{title} ({items.length})</h4>
      </div>
      <div className="max-h-48 overflow-y-auto divide-y divide-border">
        {items.map((item, idx) => (
          <div key={idx} className="px-3 py-2 text-sm flex items-start gap-2">
            <svg className={`w-4 h-4 mt-0.5 shrink-0 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
              {color === "red" ? (
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              )}
            </svg>
            <div>
              <span className="text-muted-foreground">Row {item.row}</span>
              <span className="mx-1 text-muted-foreground">·</span>
              <span className="font-medium text-foreground">{item.field}</span>
              <span className="mx-1 text-muted-foreground">·</span>
              <span className="text-muted-foreground">{item.message}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BulkImportModal;
